<template>
  <modal
    :show.sync="showRejectModal"
    modalContentClasses=""
    modalClasses="modal-alert"
    type="mini"
    @close="closeModalReject"
  >
    <div
      class="col-12 pt-4 pb-2"
      :class="{ 'is-invalid': $v.optionRejected.$error }"
    >
      <div class="text-center">
        <img
          src="~rhomtrip_assets/src/img/icons/alerts/question.png"
          alt="question"
          class="icon-modal"
        />
        <h2 class="modal-title mb-1">
          Reject Reservation
        </h2>
      </div>
      <p>Are you sure you want to Reject this reservation?</p>
      <p>To continue select a <strong>Reason:</strong></p>

      <b-form-radio-group
        id="radio-optionRejected"
        v-model="optionRejected"
        aria-describedby="ariaDescribedby"
        name="radio-optionRejected"
        stacked
        size="md"
      >
        <b-form-radio :value="options.NO_AVAILABILITY" tabindex="15">{{
          options.NO_AVAILABILITY
        }}</b-form-radio>
        <b-form-radio :value="options.WRONG_RATE">{{
          options.WRONG_RATE
        }}</b-form-radio>
        <b-form-radio :value="options.OTHER">{{ options.OTHER }}</b-form-radio>
      </b-form-radio-group>

      <small
        v-if="$v.optionRejected.$error"
        class="text-center invalid-feedback"
        >Select a reason</small
      >
      <base-alert
        type="info text-left"
        class="mb-1 info-min mb-4 p-2"
        v-if="isOther"
      >
        <i class="flaticon-circle mr-1"></i>
        Enter the reason for rejecting the reservation
      </base-alert>
      <div
        class="col-12 mt-3"
        :class="{ 'is-invalid': $v.reason.$error }"
        v-if="isOther"
      >
        <label class="title-label">Other Reason</label>
        <el-input
          v-model="reason"
          placeholder="Other Reason"
          id="OtherReason"
          name="OtherReason"
          maxlength="200"
          type="textarea"
          :autosize="{ minRows: 1, maxRows: 5 }"
          show-word-limit
        >
        </el-input>
        <small class="invalid-feedback" v-if="$v.reason.$error"
          >Enter another reason</small
        >
      </div>
      <base-alert
        type="info text-left"
        class="mb-1 info-min mb-4 p-2"
        v-if="isWrongRate"
      >
        <i class="flaticon-circle mr-1"></i>
        Enter updated rate amount
      </base-alert>
      <div
        class="col-xl-12 mt-3"
        v-if="isWrongRate"
        v-bind:class="{ 'is-invalid': $v.updateRate.$error }"
      >
        <label class="title-label">Update Rate</label>
        <BaseNumberInput
          v-model.number="$v.updateRate.$model"
          placeholder="Update Rate"
          id="updateRate"
          name="updateRate"
        />

        <small class="invalid-feedback text-left" v-if="$v.updateRate.$error"
          >Enter an updated rate</small
        >
      </div>
      <base-alert
        type="info text-left"
        class="mb-1 info-min mb-4 p-2"
        v-if="isNoAvailability"
      >
        <i class="flaticon-circle mr-1"></i>
        If you have availability on a different date/time please let us know
        below, otherwise leave blank
      </base-alert>
      <div
        class="col-xl-12 mt-3"
        v-if="isNoAvailability"
        v-bind:class="{ 'is-invalid': $v.newAvailability.$error }"
      >
        <label class="title-label">Available Date or Time</label>
        <base-input
          v-model="$v.newAvailability.$model"
          placeholder="Available Date or Time"
          id="newAvailability"
          name="newAvailability"
          maxlength="45"
        >
        </base-input>

        <small
          class="invalid-feedback text-left"
          v-if="$v.newAvailability.$error"
          >{{
            !$v.newAvailability.minLength || $v.newAvailability.$error
              ? `Minimum ${$v.newAvailability.$params.minLength.min} and maximum ${$v.newAvailability.$params.maxLength.max} characters.`
              : ""
          }}</small
        >
      </div>
    </div>
    <div
      class="border-top pt-3 text-center col-12 d-flex justify-content-around"
    >
      <base-button
        type="outline-neutral"
        class="min-button "
        @click="closeModalReject(false)"
        >No</base-button
      >

      <base-button
        type="primary"
        class="min-button ml-auto"
        @click.stop="changeStatusToReject"
        >Yes</base-button
      >
    </div>
  </modal>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import endpoint from "../../../Commons/Services/index";
import { unauthorizedAction } from "../../../../helpers/commons.helpers";
import {
  decimal,
  required,
  minLength,
  maxLength,
  minValue
} from "vuelidate/lib/validators";

const REJECT_OPTIONS = {
  NO_AVAILABILITY: "No Availability",
  WRONG_RATE: "Wrong Service Rate",
  OTHER: "Other"
};

export default {
  name: "RejectedReservation",
  props: {
    show: {
      type: Boolean,
      default: false,
      description: "(closeable)"
    },
    reservationId: {
      type: String,
      default: () => null,
      description: "(Reservation Id)"
    },
    currency: {
      type: Object,
      default: () => null,
      description: "(currency)"
    }
  },
  data() {
    return {
      options: REJECT_OPTIONS,
      optionRejected: "",
      reason: "",
      showRejectModal: false,
      updateRate: "",
      newAvailability: ""
    };
  },
  watch: {
    show: {
      handler(current) {
        this.showRejectModal = current;
      },
      immediate: true
    }
  },
  methods: {
    /**
     * Validate the form and, if valid, close the modal and submit the
     * rejection with the reason built from the selected option.
     * @returns {Promise<void>}
     */
    async changeStatusToReject() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        return;
      }
      const reason = this.getReason();
      this.closeModalReject(false);
      await this.rejectReservationById(this.reservationId, { reason });
    },
    /**
     * Send the rejection request for the reservation and notify the result.
     * @param {string} reservationId - Reservation identifier.
     * @param {Object} payload - Rejection payload (e.g. { reason }).
     * @returns {Promise<void>}
     */
    async rejectReservationById(reservationId, payload) {
      try {
        this.setLoad(true);
        const {
          data
        } = await endpoint.reservations.services.rejectReservationById(
          reservationId,
          payload,
          this.sesion.token
        );
        this.setLoad(false);
        if (!data) {
          this.$notify({
            type: "danger",
            title: "Error!",
            message: "The reservation could not be rejected",
            icon: "flaticon-remove"
          });
          this.onReservationRejected(false);
          return;
        }
        this.$notify({
          type: "success",
          title: "Success!",
          message: "The reservation has been rejected successfully",
          icon: "flaticon-check-3"
        });
        this.onReservationRejected(true);
      } catch (error) {
        this.setLoad(false);
        this.handleError(
          error,
          "The Operation could not be rejected. Please try again"
        );
        this.onReservationRejected(false);
      }
    },
    handleError(error, message) {
      if (error.response) {
        const { data } = error.response;
        if (data.code === 401) {
          if (unauthorizedAction(error)) {
            this.setSigOut(true);
          }
        } else {
          this.$notify({
            type: "danger",
            title: "Error!",
            message: "The Operation could not be completed. Please try again",
            icon: "flaticon-remove"
          });
        }
      } else {
        this.$notify({
          type: "danger",
          title: "Error!",
          message: message,
          icon: "flaticon-remove"
        });
      }
    },
    closeModalReject(status = false) {
      this.showRejectModal = status;
      this.resetData();
      this.$emit("close", status);
    },
    resetData() {
      this.optionRejected = "";
      this.reason = "";
      this.updateRate = "";
      this.newAvailability = "";
      this.$v.$reset();
    },
    getReason() {
      if (this.isNoAvailability) {
        return this.newAvailability
          ? `Available on the following date/time: ${this.newAvailability}`
          : REJECT_OPTIONS.NO_AVAILABILITY;
      }
      if (this.isWrongRate) {
        return `Update Rate: ${this.updateRate} ${this.currency.code}`;
      }
      if (this.isOther) {
        return this.reason;
      }
      return "";
    },
    onReservationRejected(status) {
      this.$emit("onReservationRejected", status);
    },
    ...mapMutations(["setLoad", "setSigOut"])
  },
  computed: {
    isNoAvailability() {
      return this.optionRejected === REJECT_OPTIONS.NO_AVAILABILITY;
    },
    isWrongRate() {
      return this.optionRejected === REJECT_OPTIONS.WRONG_RATE;
    },
    isOther() {
      return this.optionRejected === REJECT_OPTIONS.OTHER;
    },
    ...mapGetters(["sesion"])
  },
  // Only the field of the selected option is validated, so leftover input
  // in a hidden field cannot block the submit.
  validations() {
    const rules = { optionRejected: { required } };
    if (this.isOther) {
      rules.reason = { required, minLength: minLength(2), maxLength: maxLength(200) };
    } else if (this.isWrongRate) {
      rules.updateRate = { required, decimal, minValue: minValue(0.01) };
    } else if (this.isNoAvailability) {
      rules.newAvailability = { minLength: minLength(2), maxLength: maxLength(45) };
    }
    return rules;
  }
};
</script>
